import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, ExternalLink, MapPin, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ImageUpload, GalleryUpload } from '@/components/ImageUpload';
import { Link } from 'react-router-dom';

const EMPTY = { nome: '', logo: '', zona: '', descrizione: '', descrizione_en: '', gallery: [], indirizzo: '', telefono: '', sito_web: '', instagram: '' };

export default function AdminLocali() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [q, setQ] = useState('');
  const [zona, setZona] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (zona !== 'all') params.zona = zona;
    const res = await api.get('/admin/locali', { params });
    setItems(res.data || []);
  };

  useEffect(() => { api.get('/admin/zones').then((r) => setZones(r.data?.zones || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [q, zona]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (l) => { setEditing(l); setForm({ ...EMPTY, ...l }); setOpen(true); };

  const save = async () => {
    if (!form.nome || !form.zona) { toast.error('Nome e Zona sono obbligatori'); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/locali/${editing.id}`, form);
        toast.success('Locale aggiornato');
      } else {
        await api.post('/admin/locali', form);
        toast.success('Locale creato');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore di salvataggio'); }
    finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Eliminare "${l.nome}"?`)) return;
    await api.delete(`/admin/locali/${l.id}`);
    toast.success('Eliminato'); load();
  };

  return (
    <AdminLayout title="Locali">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-subtle)]"/>
          <Input data-testid="admin-locali-search" placeholder="Cerca per nome…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-[color:var(--surface)] border-[color:var(--border-c)]" />
        </div>
        <Select value={zona} onValueChange={setZona}>
          <SelectTrigger data-testid="admin-locali-filter-zone" className="sm:w-56 bg-[color:var(--surface)] border-[color:var(--border-c)]"><SelectValue placeholder="Zona"/></SelectTrigger>
          <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
            <SelectItem value="all">Tutte le zone</SelectItem>
            {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="admin-locali-new-button" onClick={startNew} className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black"><Plus className="w-4 h-4 mr-1"/>Nuovo Locale</Button>
      </div>

      <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-[color:var(--text-subtle)] bg-[color:var(--bg-2)]">
            <tr><th className="p-3">Locale</th><th className="p-3">Zona</th><th className="p-3 hidden md:table-cell">Slug</th><th className="p-3 text-right">Azioni</th></tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-c)]">
            {items.length === 0 ? (
              <tr><td colSpan="4" className="p-6 text-center text-[color:var(--text-muted)]">Nessun locale</td></tr>
            ) : items.map((l) => (
              <tr key={l.id} className="hover:bg-[rgba(214,185,140,0.04)]" data-testid="admin-locale-row">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {l.logo ? <img src={l.logo} alt={l.nome} className="w-10 h-10 rounded-md object-cover border border-[color:var(--border-c)]" /> : <div className="w-10 h-10 rounded-md bg-[color:var(--surface-2)]"/>}
                    <span className="font-medium">{l.nome}</span>
                  </div>
                </td>
                <td className="p-3"><span className="inline-flex items-center gap-1 text-xs text-[color:var(--text-muted)]"><MapPin className="w-3 h-3"/>{l.zona}</span></td>
                <td className="p-3 hidden md:table-cell text-[color:var(--text-muted)]">{l.slug}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/locali/${l.slug}`} target="_blank" className="text-xs text-[color:var(--text-subtle)] hover:text-[color:var(--gold)]"><ExternalLink className="w-4 h-4"/></Link>
                    <Button data-testid="admin-locale-edit" size="sm" variant="ghost" onClick={() => startEdit(l)}><Pencil className="w-4 h-4"/></Button>
                    <Button data-testid="admin-locale-delete" size="sm" variant="ghost" onClick={() => remove(l)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-[color:var(--border-c)] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? 'Modifica Locale' : 'Nuovo Locale'}</DialogTitle>
            <DialogDescription>Compila i dati. Lo slug viene generato automaticamente dal nome.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Field label="Nome locale *"><Input data-testid="admin-locale-form-nome" value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Zona *">
                <Select value={form.zona} onValueChange={(v) => setForm({...form, zona: v})}>
                  <SelectTrigger data-testid="admin-locale-form-zona" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"><SelectValue placeholder="Seleziona zona"/></SelectTrigger>
                  <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
                    {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Indirizzo"><Input value={form.indirizzo || ''} onChange={(e) => setForm({...form, indirizzo: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Telefono"><Input value={form.telefono || ''} onChange={(e) => setForm({...form, telefono: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Sito web"><Input value={form.sito_web || ''} onChange={(e) => setForm({...form, sito_web: e.target.value})} placeholder="https://…" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Instagram URL"><Input value={form.instagram || ''} onChange={(e) => setForm({...form, instagram: e.target.value})} placeholder="https://instagram.com/…" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
            </div>
            <div className="space-y-3">
              <ImageUpload label="Logo" value={form.logo} onChange={(v) => setForm({...form, logo: v})} testId="admin-locale-form-logo" square />
              <Field label="Descrizione (IT) — SEO"><Textarea data-testid="admin-locale-form-descrizione" value={form.descrizione || ''} onChange={(e) => setForm({...form, descrizione: e.target.value})} rows={5} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Descrizione (EN) — opzionale"><Textarea value={form.descrizione_en || ''} onChange={(e) => setForm({...form, descrizione_en: e.target.value})} rows={3} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <GalleryUpload value={form.gallery || []} onChange={(g) => setForm({...form, gallery: g})} testId="admin-locale-form-gallery" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="admin-locale-form-save" onClick={save} disabled={busy} className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black">{busy ? 'Salvataggio…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (<div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">{label}</Label>{children}</div>);
}
